import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import { ltnDialogServiceContext } from '../../../../ns-services/contexts.js';
import { LtnDialogService } from '../../../../ltn-dialog/ltn-dialog-service.js';
import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';

import { callLambda } from '../../../../helpers/lambda.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import { viewPath } from '../../../../helpers/route.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import {
  NOTES_MAX,
  notesProblem,
  publicReasonProblem,
  suppressResult,
  type LambdaAnswer,
} from './trust-score-review-logic.js';
import { recheckResult, resolveNotesProblem } from './companies-house-not-found-logic.js';
import { confirmSuppress } from './confirm-suppress.js';
import definition from '@job-schemas/support/companies-house-not-found.json';
import { jobSchema } from '../../job-schema.js';

/**
 * The nodeStream support card companies-house/trust-score-company raises when the live Companies House register has no company
 * with a newly validated company's number, so its Companies House trust score could not be calculated. The claimer checks
 * whether they can resolve it, can re-check Companies House (companies-house/trust-score/rescore), finds out why the score is
 * failing if the company does exist, and marks it resolved with notes, or suppresses the score (trust-score/suppress) when they
 * can't resolve it. A successful re-check leaves the card open: support resolves it themselves.
 *
 * The notes go with the outcome as its `notes` value (job/complete stores them on the job's outcomeValues), as the connect
 * request card hands on its notes; Suppress also gives them to trust-score/suppress, as the trust-score review card does.
 */
@customElement(NSSupportCompaniesHouseNotFound.is)
export default class NSSupportCompaniesHouseNotFound extends NSJobComponent {
  static get is() {
    return 'ns-support-companies-house-not-found';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' }, suppressed: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .steps {
        margin: 0;
        padding-left: var(--wa-space-l);
      }

      .steps li + li {
        margin-top: var(--wa-space-s);
      }
    `,
  ];

  @consume({ context: ltnDialogServiceContext })
  private __dialog?: LtnDialogService;

  @state()
  private __busy = false;

  @state()
  private __rechecking = false;

  @state()
  private __notes = '';

  @state()
  private __message: string | null = null;

  private get __companyId(): string | null {
    const id = this._job?.inputValues?.subjectCompanyId;
    return typeof id === 'string' && id ? id : null;
  }

  private get __companyName(): string {
    const name = this._job?.inputValues?.subjectCompanyName;
    return typeof name === 'string' && name ? name : 'This company';
  }

  private get __companyNumber(): string | null {
    const number = this._job?.inputValues?.companyNumber;
    return typeof number === 'string' && number ? number : null;
  }

  private get __claimedByMe(): boolean {
    const me = this._directory?.me?.id;
    return !!me && !!this._job && this._job.assignedTo === me;
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __call(path: string, body: object): Promise<LambdaAnswer> {
    const response = await callLambda(path, 'POST', this._auth?.token, JSON.stringify(body));
    return response.res as unknown as LambdaAnswer;
  }

  private async __recheck() {
    const companyId = this.__companyId;
    if (!companyId || this.__busy || this.__rechecking) return;
    this.__rechecking = true;
    this.clearError();
    this.__message = null;
    try {
      const result = recheckResult(await this.__call('companies-house/trust-score/rescore', { companyId }));
      if (result.ok) this.__message = result.message;
      else this.showError(result.message);
    } catch (err) {
      this.showError(err, "Companies House couldn't be re-checked. Please try again.");
    } finally {
      this.__rechecking = false;
    }
  }

  private async __resolve() {
    if (this.__busy || this.__rechecking) return;
    const problem = resolveNotesProblem(this.__notes);
    if (problem) {
      this.showError(problem);
      return;
    }
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved', { notes: this.__notes.trim() });
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  private async __suppress() {
    const companyId = this.__companyId;
    if (!companyId || this.__busy || this.__rechecking) return;
    const problem = notesProblem(this.__notes, true);
    if (problem) {
      this.showError(problem);
      return;
    }
    const publicReason = await confirmSuppress(this.__dialog);
    if (publicReason === null) return;
    const reasonProblem = publicReasonProblem(publicReason);
    if (reasonProblem) {
      this.showError(reasonProblem);
      return;
    }
    this.__busy = true;
    this.clearError();
    this.__message = null;
    this.loading('Suppressing the trust score');
    try {
      const notes = this.__notes.trim();
      const result = suppressResult(
        await this.__call('trust-score/suppress', { companyId, notes, publicReason: publicReason.trim() }),
      );
      this.loaded();
      if (result.kind !== 'done') {
        this.showError(result.message);
        return;
      }
      this.__message = result.message;
      try {
        await this.outcome('suppressed', { notes });
      } catch (err) {
        this.showError(err, `${result.message} This card couldn't be completed: please try again.`);
      }
    } catch (err) {
      this.loaded();
      this.showError(err, "The score couldn't be suppressed. Please try again.");
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const companyId = this.__companyId;
    const number = this.__companyNumber;
    const mine = this.__claimedByMe && this._job?.status !== 'COMPLETE';
    return html`
      <ns-job-layout>
        <p slot="intro">
          Not on the Companies House register:
          ${companyId
            ? html`
                <a
                  href="/company/${companyId}"
                  @click=${(e: Event) => {
                    e.preventDefault();
                    viewPath(`/company/${companyId}`);
                  }}
                >
                  ${this.__companyName}
                </a>
              `
            : this.__companyName}
        </p>
        ${this.__message
          ? html`
              <div class="job-panel">
                <p role="status">${this.__message}</p>
              </div>
            `
          : nothing}
        <div class="job-panel">
          <h3>Company number not found</h3>
          <ns-detail-list
            layout="grid"
            .rows=${[
              { label: 'Company', value: this.__companyName },
              { label: 'Company number', value: number ?? 'Unknown' },
            ]}
          ></ns-detail-list>
          <p>
            ${number ? `Company number ${number}` : "This company's number"} is not on the live Companies House
            register, so ${this.__companyName}'s Companies House trust score could not be calculated.
          </p>
        </div>
        <div class="job-panel">
          <h3>What to do</h3>
          <ol class="steps">
            <li>Check whether you can resolve it, for example if the company number was entered wrongly.</li>
            <li>
              Re-check Companies House. If it now has the company, the trust score is calculated and goes to the
              trust-score review queue.
              ${mine
                ? html`
                    <div>
                      <wa-button
                        variant="brand"
                        appearance="outlined"
                        ?disabled=${this.__busy || this.__rechecking}
                        ?loading=${this.__rechecking}
                        @click=${() => this.__recheck()}
                      >
                        Re-check Companies House
                      </wa-button>
                    </div>
                  `
                : nothing}
            </li>
            <li>If the company does exist on the register, find out why its trust score is failing.</li>
            <li>If you can't resolve it, suppress the score.</li>
          </ol>
        </div>
        ${mine
          ? html`
              <div class="job-panel">
                <wa-textarea
                  label="Notes (required)"
                  hint="Say what you found and what you did. They stay internal."
                  maxlength=${NOTES_MAX}
                  .value=${this.__notes}
                  ?disabled=${this.__busy}
                  @input=${(e: Event) => {
                    this.__notes = (e.target as HTMLTextAreaElement).value;
                  }}
                ></wa-textarea>
              </div>
            `
          : nothing}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>About this card</h3>
          <ul class="ticks">
            <li>
              <strong>Resolved</strong>
              closes this card once you have sorted it out. Your notes are kept with it.
            </li>
            <li>
              <strong>Suppress</strong>
              shows every company a trust score of 0 with your public reason, and stops its re-checks, until it is
              published again.
            </li>
            <li>Re-checking Companies House leaves this card open.</li>
          </ul>
          <p class="note">Only the person who holds the card can act; claim it first if it is waiting for the team.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    if (!this.__claimedByMe) return nothing;
    const disabled = this.__busy || this.__rechecking;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${disabled} @click=${() => this.__resolve()}>
        Resolved
      </wa-button>
      <wa-button
        variant="brand"
        appearance="outlined"
        type="button"
        ?disabled=${disabled}
        @click=${() => this.__suppress()}
      >
        Suppress
      </wa-button>
    `;
  }
}
